(function () {
  // Guard against redeclaring the classes / redefining the custom elements if
  // this section (and so its own <script src> tag) is on a page more than once.
  if (window.MarqueeVideoShowcaseLoaded) return;
  window.MarqueeVideoShowcaseLoaded = true;

  const REDUCED_MOTION = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * <wave-marquee> — text that travels along the wave, not sideways.
   *
   * The reference animation slides a whole SVG left; every letter keeps the
   * same height and only the frame moves. Here the <path> never moves at all:
   * we animate the <textPath>'s own startOffset, so each glyph is re-placed
   * further along the curve on every frame and physically climbs each crest
   * and drops into each trough as it goes.
   *
   * Seamless looping without a visible reset: the text is one phrase repeated
   * enough times to cover the whole path PLUS one extra phrase, so once the
   * offset has travelled exactly one phrase-width we can snap it back to the
   * start — the glyph under any given point of the curve is identical before
   * and after the snap, so there is nothing to see.
   * ------------------------------------------------------------------ */
  class WaveMarquee extends HTMLElement {
    connectedCallback() {
      this.path = this.querySelector('.marquee-video__wave-path');
      this.textEl = this.querySelector('.marquee-video__wave-text');
      this.textPath = this.querySelector('textPath');
      if (!this.path || !this.textEl || !this.textPath) return;

      this.svg = this.querySelector('svg');
      // Non-breaking spaces, because SVG collapses a run of ordinary ones and
      // the repeats would end up butted together.
      this.unit = `${(this.dataset.text || '').trim()}\u00a0\u00a0\u00a0`;
      this.speed = parseFloat(this.dataset.speed) || 60; // user units per second
      this.direction = this.dataset.direction === 'right' ? 1 : -1;
      this.offset = 0;
      this.unitLength = 0;
      this.tick = this.tick.bind(this);

      // Web fonts change the measured phrase width, so measure once they are
      // in — otherwise the loop length is computed against the fallback font
      // and the seam stops lining up.
      const measure = () => this.measure();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure).catch(measure);
      } else {
        measure();
      }

      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize);

      // Only burn frames while the section is actually on screen.
      this.observer = new IntersectionObserver((entries) => {
        // entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          this.isVisible ? this.start() : this.stop();
        });
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    /* The SVG is stretched to span the screen, so one user unit is worth more
       than one pixel on a wide monitor — leave the font size alone and the
       words balloon on desktop while staying small on a phone. The font is
       therefore set in user units scaled by however far the SVG has been
       stretched, which lands the words at their stylesheet size on every
       screen. Must run before the text is measured below, since it
       changes how wide a phrase is. */
    scaleFont() {
      const viewBox = this.svg?.viewBox?.baseVal?.width || 1600;
      const scale = this.clientWidth / viewBox;
      if (!scale) return;
      // Clear our own override first, so what we read back is the size the
      // stylesheet asks for — which is .subtitle's, in rem, and so different
      // at each breakpoint. Reading it rather than being told it means the
      // breakpoints live in one place.
      this.textEl.style.fontSize = '';
      const target = parseFloat(getComputedStyle(this.textEl).fontSize);
      if (!target) return;
      this.textEl.style.fontSize = `${target / scale}px`;
    }

    measure() {
      if (!this.isConnected) return;
      this.scaleFont();
      const pathLength = this.path.getTotalLength();
      if (!pathLength) return;

      // Width of a single phrase, measured for real rather than guessed.
      this.textPath.textContent = this.unit;
      const unitLength = this.textEl.getComputedTextLength();
      if (!unitLength) return;
      this.unitLength = unitLength;

      // Cover the path plus one spare phrase, so there is always text queued
      // up beyond both ends of the visible curve.
      const copies = Math.ceil((pathLength + unitLength) / unitLength) + 1;
      this.textPath.textContent = this.unit.repeat(copies);

      this.offset = this.direction === 1 ? -unitLength : 0;
      this.textPath.setAttribute('startOffset', this.offset);
      // this.start();
      // PageSpeed: measure() runs after fonts load and on every resize (mobile URL-bar scroll fires
      // resize too), which used to restart the frame loop even while the section was off screen.
      if (this.isVisible) this.start();
    }

    start() {
      if (this.rafId || !this.unitLength) return;
      if (REDUCED_MOTION()) return;
      this.lastTime = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }

    tick(now) {
      if (this.lastTime) {
        const delta = Math.min((now - this.lastTime) / 1000, 0.1); // ignore tab-switch gaps
        this.offset += this.direction * this.speed * delta;
        // One phrase travelled === back to a visually identical state.
        if (this.offset <= -this.unitLength) this.offset += this.unitLength;
        if (this.offset >= 0 && this.direction === 1) this.offset -= this.unitLength;
        this.textPath.setAttribute('startOffset', this.offset.toFixed(2));
      }
      this.lastTime = now;
      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  /* ------------------------------------------------------------------ *
   * <house-zoom> — the section opens out of a little house as you scroll.
   *
   * The wrapper is a scroll runway; the viewport inside it is pinned for the
   * length of that runway, so "progress" is simply how far through the runway
   * the visitor has scrolled — 0 when the pin engages, 1 when it releases.
   * Nothing here is on a timer: scroll back up and the house shrinks again.
   *
   * The house is grown by writing its width and height in pixels rather than
   * by scaling it. A scaled house would have to go from square to full-screen
   * tall, which stretches the roof into a spike; sizing it for real lets the
   * roof keep its own pitch at each end (--roof is interpolated too).
   *
   * The width finishes ahead of the height, matching the reference: the house
   * reaches the edges of the screen first and then keeps growing downwards
   * while the contents fade up inside it at their final size.
   * ------------------------------------------------------------------ */
  const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
  const lerp = (from, to, t) => from + (to - from) * t;
  // Slow to start, quick to finish — the house creeps in, then rushes open.
  const ease = (t) => Math.pow(t, 1.6);
  // How the runway is spent. The house is fully open by OPEN_AT, the contents
  // fade up over the REVEAL that follows, and what is left is a hold: the
  // finished section standing full-screen, scalloped edge on the bottom of the
  // screen, before the pin releases and the page moves on. Without that hold
  // the section is only ever complete at the very pixel it starts to leave.
  const OPEN_AT = 0.4;
  const REVEAL = 0.2;
  // How far the phone and its previews may travel from their designed size, if
  // the stylesheet does not say. It says per breakpoint, because a phone screen
  // has far less height to give than a monitor.
  const MIN_MEDIA = 0.72;
  const MAX_MEDIA = 1.3;

  class HouseZoom extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.marquee-video__viewport');
      this.house = this.querySelector('.marquee-video__house');
      if (!this.viewport || !this.house) return;

      this.content = this.querySelector('.marquee-video__content');
      this.carousel = this.querySelector('.marquee-video__content > .video-carousel');
      this.heading = this.querySelector('.video-carousel__heading-wrap');
      this.phone = this.querySelector('.video-carousel__phone');
      this.wrapper = this.querySelector('.video-carousel__wrapper');
      this.track = this.querySelector('.video-carousel__track');
      this.handle = this.querySelector('.video-carousel__handle');

      this.enabled = this.dataset.zoom === 'true' && !REDUCED_MOTION();
      if (!this.enabled) return;

      // A roof pitch of 0 is meaningful — it is what flattens the house into a
      // full-bleed background — so these cannot fall back on `||`.
      const percent = (value, fallback) => {
        const parsed = parseFloat(value);
        return (Number.isFinite(parsed) ? parsed : fallback) / 100;
      };
      this.startSize = percent(this.dataset.startSize, 54);
      this.startWidth = parseFloat(this.dataset.startWidth) || 260;
      this.roofStart = percent(this.dataset.roofStart, 37);
      this.roofEnd = percent(this.dataset.roofEnd, 0);

      this.onScroll = () => this.request();
      // Mobile browsers fire resize every time the address bar slides, so the
      // re-measure is held to one a frame like the scroll handler is - and is
      // then skipped altogether unless the stage really did change size. The
      // stage is sized in svh, so an address bar sliding away no longer
      // changes it; re-fitting the carousel anyway, mid-scroll, is what made
      // the phone and its previews jump about on a real device.
      this.onResize = () => {
        if (this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = undefined;
          const width = this.viewport.clientWidth || window.innerWidth;
          const height = this.viewport.clientHeight || window.innerHeight;
          if (width === this.stageWidth && height === this.stageHeight) return;
          this.measure();
          this.update();
        });
      };

      this.measure();
      this.update();
      this.classList.add('is-ready');

      // The heading is set in a web font, so its height — and with it the room
      // left for the phone — is only final once that font has arrived.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.fitStage()).catch(() => {});
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = undefined;
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = undefined;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    measure() {
      this.stageWidth = this.viewport.clientWidth || window.innerWidth;
      this.stageHeight = this.viewport.clientHeight || window.innerHeight;
      // The small house is roughly square, the way it is in the reference, and
      // it is a real size rather than a share of the screen — a percentage
      // makes it a modest house on a phone and a vast one on a monitor. The
      // percentage only steps in on screens too narrow to fit that size.
      this.seedWidth = Math.min(this.startWidth, this.stageWidth * this.startSize);
      // The scroll cue hangs off the small house, so the stylesheet needs its size.
      this.style.setProperty('--seed-size', `${this.seedWidth}px`);
      // Everything above one screenful of the wrapper is runway to scroll through.
      this.runway = Math.max(1, this.offsetHeight - this.stageHeight);
      // The contents are laid out for the finished, full-screen house and then
      // simply faded in, so they must not be re-flowed by the growing box.
      this.style.setProperty('--stage-w', `${this.stageWidth}px`);
      this.style.setProperty('--stage-h', `${this.stageHeight}px`);
      this.fitStage();
    }

    /* Fitting the carousel to the stage.

       The carousel is drawn for one window height. On a taller one it would
       float in the middle with a band of bare stripes above and below it; on a
       shorter one it would overflow, the heading clipped by the roof and the
       button by the scalloped edge. The spacing is not what moves: the 3rem
       gaps either side of the carousel (7rem under the button on mobile) and
       the padding from the section's own settings stay exactly as set. The
       phone and its previews take up the difference instead — growing into the
       spare height, shrinking to make room — between the bounds the stylesheet
       gives per breakpoint, so the stage is filled either way. */
    fitStage() {
      if (!this.content || !this.carousel || !this.phone) return;
      this.style.setProperty('--media-scale', 1);

      const styles = getComputedStyle(this.content);
      const padding = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      /* Measure against the stage the contents are laid out for - the finished,
         full-screen house - and not the box the house happens to be at this
         instant. Mid-zoom the house is only --seed-size tall, so reading the
         content's own height then makes every figure below wildly negative and
         pins the phone at its smallest allowed size. That is what broke the
         section on a resize (and on a phone, where the address bar sliding
         away fires resize mid-scroll), and on the first visit of a session:
         the re-fit that waits on the web font lands after the visitor has
         already started scrolling, so it measured a half-grown house. On a
         reload the font is cached, the re-fit happens while the house is still
         full size, and the section looks right - which is why refreshing
         appeared to fix it.

         The content sits at top: var(--stage-top); bottom: 0 inside the house,
         so its finished height is the stage less that offset. */
      const stageTop = parseFloat(styles.top) || 0;
      const spare = this.stageHeight - stageTop - this.carousel.offsetHeight - padding;

      const media = this.phone.offsetHeight;
      if (media <= 0 || !spare) return;

      const bound = (name, fallback) => {
        const value = parseFloat(getComputedStyle(this).getPropertyValue(name));
        return Number.isFinite(value) ? value : fallback;
      };
      const scale = Math.min(
        bound('--media-max', MAX_MEDIA),
        this.widthLimit(),
        Math.max(bound('--media-min', MIN_MEDIA), (media + spare) / media)
      );
      this.style.setProperty('--media-scale', scale.toFixed(3));
    }

    /* Spare height is no use if the row cannot get any wider: the phone and its
       two previews sit side by side, and past a point they would run into the
       arrows and off the page. So growth is also held to what is left across
       the row once the arrows and the gaps — none of which scale — have taken
       their share. On a phone the design runs the previews off both edges of
       the screen on purpose, so there is nothing to hold it to. */
    widthLimit() {
      if (!this.wrapper || !this.track || window.innerWidth < 750) return Infinity;

      let media = 0;
      this.track.querySelectorAll('.video-carousel__phone, .video-carousel__preview').forEach((el) => {
        media += el.offsetWidth;
      });
      if (media <= 0) return Infinity;

      const gap = parseFloat(getComputedStyle(this.wrapper).columnGap) || 0;
      let chrome = Math.max(this.track.scrollWidth, this.track.offsetWidth) - media;
      this.wrapper.querySelectorAll('.video-carousel__arrow').forEach((el) => {
        chrome += el.offsetWidth + gap;
      });

      return (this.wrapper.clientWidth - chrome) / media;
    }

    request() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = undefined;
        this.update();
      });
    }

    update() {
      const top = this.getBoundingClientRect().top;
      const progress = clamp01(-top / this.runway);
      const eased = ease(progress);
      // The house has finished growing with a good deal of the runway still to
      // go, so the heading, the carousel and the scalloped bottom edge appear
      // over a section that is already open to the full screen — and once they
      // are all in, the section simply holds there until the pin releases.
      const opened = clamp01(eased / OPEN_AT);
      // Width leads so the house hits the edges of the screen first, then goes
      // on growing downwards.
      const wide = clamp01(opened * 1.25);

      const width = lerp(this.seedWidth, this.stageWidth, wide);
      const height = lerp(this.seedWidth, this.stageHeight, opened);
      const roof = lerp(this.seedWidth * this.roofStart, this.stageWidth * this.roofEnd, wide);

      this.house.style.setProperty('--house-w', `${width.toFixed(1)}px`);
      this.house.style.setProperty('--house-h', `${height.toFixed(1)}px`);
      this.house.style.setProperty('--roof', `${roof.toFixed(1)}px`);
      // The ticker is gone by the time the roof would reach it.
      this.style.setProperty('--wave-opacity', (1 - clamp01(opened / 0.5)).toFixed(3));
      this.style.setProperty('--content-opacity', clamp01((eased - OPEN_AT) / REVEAL).toFixed(3));
    }
  }

  if (!customElements.get('wave-marquee')) customElements.define('wave-marquee', WaveMarquee);
  if (!customElements.get('house-zoom')) customElements.define('house-zoom', HouseZoom);

  if (window.Shopify && Shopify.designMode) {
    const reload = (root) => {
      root.querySelectorAll('wave-marquee, house-zoom').forEach((el) => {
        el.disconnectedCallback();
        el.connectedCallback();
      });
    };

    document.addEventListener('shopify:section:load', (event) => reload(event.target));
    document.addEventListener('shopify:section:reorder', () => reload(document));

  }
})();
